import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState } from 'src/store/index';
import _ from 'lodash';
import { selector as questionSelector } from 'src/store/model/question';
import { ASSESSMENT_SCORE, ASSESSMENT_TIME } from 'src/constants/app';
import dayjs from 'src/libs/dayjs';

export type State = {
  loading: boolean;
  start_at?: string | null;
  score: {
    total: number;
    correct: number;
  };
  status: 'passed' | 'failed' | 'idle' | 'timeout' | 'started';
};

export const adapter = createEntityAdapter<Model.Assessment>({
  selectId: (model) => model.id,
});

const { reducer, actions } = createSlice({
  name: 'final_assessment',
  initialState: adapter.getInitialState<State>({
    loading: false,
    score: {
      correct: 0,
      total: 1,
    },
    status: 'idle',
  }),
  reducers: {
    updateEntities(state, action: PayloadAction<Model.Assessment[]>) {
      adapter.upsertMany(state, action);
    },
    updateEntity(state, action: PayloadAction<Model.Assessment>) {
      adapter.upsertOne(state, action);
    },
    removeEntity(state, action: PayloadAction<Model.Assessment>) {
      adapter.removeOne(state, action.payload.id);
    },
    updateLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    resetEntities(state) {
      adapter.removeAll(state);
      state.score = { correct: 0, total: 1 };
      state.start_at = null;
      state.status = 'idle';
    },
    updateScore(state, action: PayloadAction<[number, number]>) {
      const [correct, total] = action.payload;
      state.score = {
        correct,
        total,
      };
      state.status = correct / total < ASSESSMENT_SCORE ? 'failed' : 'passed';
    },
    updateStartAt(state, action: PayloadAction<string | null | undefined>) {
      state.start_at = action.payload;
    },
    updateStatus(state, action: PayloadAction<State['status']>) {
      state.status = action.payload;
    },
  },
});

export const selector = adapter.getSelectors(
  (state: RootState) => state.model.final_assessment,
);

export const selectFinalAssessmentByClassId = createSelector(
  questionSelector.selectAll,
  (state: RootState, class_id?: string) => class_id,
  (quizzes, class_id) => {
    const result = quizzes.filter(
      (q) =>
        q.class_id === class_id &&
        !q.class_topic_id &&
        !q.class_topic_content_id,
    );

    return _.orderBy(result, ['sequence'], 'asc');
  },
);

export const selectQuestionWithAssessment = createSelector(
  questionSelector.selectAll,
  selector.selectAll,
  (questions, assessments) => {
    const assessmentMap = new Map(assessments.map((a) => [a.question_id, a]));
    const questionMap = new Map(questions.map((a) => [a.id, a]));

    return (question_id: string) => {
      return {
        ...questionMap.get(question_id),
        draft: assessmentMap.get(question_id),
      };
    };
  },
);

export const selectQuizzesByGroup = createSelector(
  selectFinalAssessmentByClassId,
  (state: RootState) => state.model.final_assessment.start_at,
  (state: RootState, class_id: string, per?: number) => per,
  (quizzes, start_at, per = 5) => {
    const chunk = _.chunk(quizzes, per);

    const questionMap = new Map(chunk.map((q, i) => [i, q]));

    let startAt = dayjs().isAfter(dayjs(start_at).add(ASSESSMENT_TIME, 's'))
      ? null
      : start_at;

    return {
      total: quizzes.length,
      questionMap,
      start_at: startAt,
      per,
    };
  },
);

export { reducer };
export default actions;
